import React, { useEffect, useState } from 'react';
import EditorialDetailsPageViewLoader from '../../../../../../core/components/editorial/editorialdetails/pageviews/EditorialDetailsPageViewLoader';
import EditorialDetailsPageViewController from '../../../../../../core/components/editorial/editorialdetails/pageviews/EditorialDetailsPageViewController';
import { LoadingPage } from '../../../../../shared/LoadingPage';
import ErrorPage from '../../../../../shared/ErrorPage';
import DetailBar from './components/DetailBar';
import EditorialSummaryChart from '../../components/EditorialSummaryChart';
import ComparisonControls from '../../components/ComparisonControls';
import moment from 'moment';
import { Col, Row } from 'antd';
import PageViewsTable from './components/PageViewsTable';
import {
  COMPARISON_MODES,
  getComparisonRange,
  buildComparisonSeries,
  computeDeltas,
} from '../../utils/comparison';

const PV_DELTA_KEYS = ['pageViews', 'clicks', 'customers'];
const PV_CHART_KEYS = ['clicks', 'pageViews', 'customers'];

const day = (d) => moment(d).format('YYYY-MM-DD');
const filterByRange = (rows, field, [start, end]) => {
  const startStr = day(start);
  const endStr = day(end);
  return (rows || []).filter((r) => {
    const t = moment(r[field], 'YYYY-MM-DD');
    return t.isSameOrAfter(startStr) && t.isSameOrBefore(endStr);
  });
};

const EditorialPageViewDetailsView = (props) => {
  return (
    <EditorialDetailsPageViewLoader {...props}>
      <EditorialDetailsPageViewController>
        <PageViewsTab />
      </EditorialDetailsPageViewController>
    </EditorialDetailsPageViewLoader>
  );
};

const PageViewsTab = ({
  reportData,
  pageViewsListNormalized,
  reportLoading,
  reportError,
  startDate,
  endDate,
  filterByDate,
}) => {
  const [summaryData, setSummaryData] = useState();
  const [pageViewList, setPageViewList] = useState();
  const [currentRange, setCurrentRange] = useState([moment(startDate), moment(endDate)]);
  const [compareMode, setCompareMode] = useState(COMPARISON_MODES.OFF);
  const [customRange, setCustomRange] = useState(null);
  const [deltas, setDeltas] = useState(null);
  const [comparisonByDate, setComparisonByDate] = useState([]);

  useEffect(() => {
    if (reportData) setSummaryData(reportData.summary);
    if (pageViewsListNormalized) setPageViewList(pageViewsListNormalized);
  }, [reportData, pageViewsListNormalized]);

  const byDates = reportData && reportData.byDates;

  useEffect(() => {
    if (!byDates || !compareMode || compareMode === COMPARISON_MODES.OFF) {
      setDeltas(null);
      setComparisonByDate([]);
      return;
    }
    const comparisonRange = getComparisonRange(compareMode, currentRange, customRange);
    if (!comparisonRange) {
      setDeltas(null);
      setComparisonByDate([]);
      return;
    }
    const cur = filterByDate(currentRange[0], currentRange[1]);
    const cmp = filterByDate(comparisonRange[0], comparisonRange[1]);
    if (cur && cmp) {
      setDeltas(computeDeltas(cur.summary, cmp.summary, PV_DELTA_KEYS, comparisonRange));
    }
    setComparisonByDate(
      buildComparisonSeries(
        filterByRange(byDates, 'date', currentRange),
        filterByRange(byDates, 'date', comparisonRange),
        currentRange,
        comparisonRange,
        PV_CHART_KEYS,
        'date'
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareMode, customRange, currentRange, byDates]);

  if (reportLoading || !reportData) return <LoadingPage />;
  if (reportError) return <ErrorPage />;

  const dropDownOptions = [
    { key: 'clicks', display: 'Impressions' },
    { key: 'pageViews', display: 'Page Views' },
    { key: 'customers', display: 'Customers' },
  ];

  const comparisonLabel = deltas && deltas.pageViews ? deltas.pageViews.ranges : '';

  return (
    <React.Fragment>
      <DetailBar startDate={startDate} endDate={endDate} {...summaryData} deltas={deltas} />
      <ComparisonControls
        mode={compareMode}
        onModeChange={(mode) => setCompareMode(mode)}
        customRange={customRange}
        onCustomRangeChange={(dates) => setCustomRange(dates)}
        comparisonLabel={comparisonLabel}
      />
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <EditorialSummaryChart
            id={'pageViews'}
            byDate={byDates}
            xAxis="date"
            options={dropDownOptions}
            defaultLeftYValue={dropDownOptions[0]}
            defaultRightYValue={dropDownOptions[1]}
            has6MonDisables={false}
            allTimeDateRange={[
              moment(startDate).format('YYYY-MM-DD'),
              moment(endDate).format('YYYY-MM-DD'),
            ]}
            comparisonByDate={comparisonByDate}
            onDatePickerChange={({ dates }) => {
              setCurrentRange(dates);
              const result = filterByDate(dates[0], dates[1]);
              if (result) {
                setSummaryData(result.summary);
                setPageViewList(result.pageViewsList);
              }
            }}
          />
        </Col>
        <Col xs={24}>
          <PageViewsTable data={pageViewList} />
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default EditorialPageViewDetailsView;
